import { useEffect, useState } from "react";
import { FileText, Activity, Users, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

export default function MyHistory() {
  const [history, setHistory] = useState([]);
  const [rxs, setRxs] = useState([]);
  const [exams, setExams] = useState([]);
  const [refs, setRefs] = useState([]);
  useEffect(() => {
    api.get("/me/history").then(({ data }) => setHistory(data || [])).catch(() => {});
    api.get("/prescriptions").then(({ data }) => setRxs(data || [])).catch(() => {});
    api.get("/exam-orders").then(({ data }) => setExams(data || [])).catch(() => {});
    api.get("/referrals").then(({ data }) => setRefs(data || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 fade-up" data-testid="my-history-page">
      <div>
        <h1 className="font-display text-3xl flex items-center gap-2"><FileText className="w-7 h-7 text-emerald-700" /> Mi historia clínica</h1>
        <p className="text-sm text-muted-foreground mt-1">Todas tus entradas médicas, recetas y pruebas en un solo lugar.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Entradas", value: history.length, color: "emerald" },
          { label: "Recetas", value: rxs.length, color: "blue" },
          { label: "Pruebas", value: exams.length, color: "violet" },
          { label: "Derivaciones", value: refs.length, color: "amber" },
        ].map((s) => (
          <Card key={s.label} className={`bg-gradient-to-br from-${s.color}-50 to-white border-${s.color}-100`}>
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-foreground">{s.value}</div>
              <div className="text-xs uppercase text-muted-foreground mt-1">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-emerald-700" /> Entradas clínicas</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {history.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">Sin entradas todavía</div>}
          {history.map((h) => (
            <div key={h.id} className="border rounded-lg p-4 space-y-2" data-testid={`my-entry-${h.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{h.title}</div>
                  <div className="text-xs text-muted-foreground">{(h.created_at || "").slice(0, 16).replace("T", " ")} · Dr. {h.doctor_name}</div>
                </div>
                <Badge variant="outline">{h.type}</Badge>
              </div>
              {h.vital_signs && Object.values(h.vital_signs).some((v) => v != null) && (
                <div className="flex flex-wrap gap-2 text-xs">
                  {h.vital_signs.ta_sys && h.vital_signs.ta_dia && <span className="px-2 py-0.5 bg-slate-100 rounded">TA {h.vital_signs.ta_sys}/{h.vital_signs.ta_dia}</span>}
                  {h.vital_signs.fc && <span className="px-2 py-0.5 bg-slate-100 rounded">FC {h.vital_signs.fc}</span>}
                  {h.vital_signs.temp && <span className="px-2 py-0.5 bg-slate-100 rounded">T {h.vital_signs.temp}°C</span>}
                  {h.vital_signs.sat_o2 && <span className="px-2 py-0.5 bg-slate-100 rounded">SatO₂ {h.vital_signs.sat_o2}%</span>}
                </div>
              )}
              {h.icd10 && h.icd10.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {h.icd10.map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded font-mono">{c}</span>)}
                </div>
              )}
              <div className="text-sm whitespace-pre-wrap">{h.content || h.notes}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Mis dependientes</CardTitle></CardHeader>
          <CardContent><Link to="/app/dependientes"><Button variant="outline" size="sm">Gestionar familiares <ArrowRight className="w-3 h-3 ml-1" /></Button></Link></CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ArrowRight className="w-4 h-4" /> Derivaciones</CardTitle></CardHeader>
          <CardContent>
            {refs.length === 0 ? <span className="text-sm text-muted-foreground">Sin derivaciones activas</span> : (
              <ul className="text-sm space-y-1">
                {refs.slice(0, 3).map((r) => <li key={r.id}>→ {r.specialty} · {r.urgency}</li>)}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
